(function () {
  if (window.CaseLabVibe) return;

  const PRIMARY_KEY = 'auracase-primary-color';
  const THEME_KEY = 'auracase-theme';
  const DEFAULT_PRIMARY = '#ff058a';
  const DEFAULT_THEME = 'dark';
  const THEMES = ['dark', 'light'];
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

  try {
    if (!localStorage.getItem(THEME_KEY) && localStorage.getItem('caselab-theme')) {
      localStorage.setItem(THEME_KEY, localStorage.getItem('caselab-theme'));
    }
    localStorage.removeItem('auracase-vibe');
    localStorage.removeItem('caselab-vibe');
  } catch (e) {}

  function normalizeTheme(value) {
    return THEMES.includes(value) ? value : DEFAULT_THEME;
  }

  function normalizeHex(value) {
    if (!value || typeof value !== 'string') return DEFAULT_PRIMARY;
    let hex = value.trim().toLowerCase();
    if (hex.charAt(0) !== '#') hex = '#' + hex;
    if (/^#[0-9a-f]{3}$/.test(hex)) {
      hex =
        '#' +
        hex
          .slice(1)
          .split('')
          .map((c) => c + c)
          .join('');
    }
    if (!/^#[0-9a-f]{6}$/.test(hex) || hex === '#5b2d82' || hex === '#c41e3a') {
      return DEFAULT_PRIMARY;
    }
    return hex;
  }

  function hexToRgbChannels(hex) {
    const raw = normalizeHex(hex).slice(1);
    const n = parseInt(raw, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function contrastChannels(r, g, b) {
    const y = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return y > 0.55 ? '18, 18, 18' : '255, 255, 255';
  }

  function readCookie(name) {
    try {
      const parts = (document.cookie || '').split(';');
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();
        if (!part) continue;
        const eq = part.indexOf('=');
        if (eq === -1) continue;
        if (part.slice(0, eq) !== name) continue;
        return decodeURIComponent(part.slice(eq + 1));
      }
    } catch (e) {}
    return null;
  }

  function writeCookie(name, value) {
    try {
      document.cookie =
        name +
        '=' +
        encodeURIComponent(value) +
        '; path=/; max-age=' +
        COOKIE_MAX_AGE +
        '; SameSite=Lax';
    } catch (e) {
      /* ignore */
    }
  }

  function readStored(key, fallback) {
    try {
      const fromLs = localStorage.getItem(key);
      if (fromLs) return fromLs;
    } catch (e) {}
    try {
      const fromSs = sessionStorage.getItem(key);
      if (fromSs) return fromSs;
    } catch (e) {}
    const fromCookie = readCookie(key);
    if (fromCookie) return fromCookie;
    return fallback;
  }

  function writeStored(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* iOS private / blocked storage — cookie below still persists */
    }
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
    writeCookie(key, value);
  }

  function applyPrimaryColor(hex) {
    const next = normalizeHex(hex);
    const [r, g, b] = hexToRgbChannels(next);
    const rgb = `${r}, ${g}, ${b}`;
    const contrast = contrastChannels(r, g, b);
    const root = document.documentElement;

    // important beats any later stylesheet that resets html { --color-primary }
    root.style.setProperty('--color-primary', rgb, 'important');
    root.style.setProperty('--vibe-ambient', rgb, 'important');
    root.style.setProperty('--color-primary-contrast', contrast, 'important');
    root.style.setProperty('--color-button', rgb, 'important');
    root.style.setProperty('--color-button-text', contrast, 'important');
    root.setAttribute('data-primary-color', next);
    writeStored(PRIMARY_KEY, next);
    syncControls();
  }

  function applyTheme(theme) {
    const next = normalizeTheme(theme);
    const root = document.documentElement;
    root.classList.add('ac-theme-animate');
    root.setAttribute('data-theme', next);
    root.style.colorScheme = next === 'light' ? 'light' : 'dark';
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute('content', next === 'light' ? '#e8e9ec' : '#0e0e10');
    writeStored(THEME_KEY, next);
    syncControls();
    window.setTimeout(() => root.classList.remove('ac-theme-animate'), 400);
  }

  function syncControls() {
    const hex = normalizeHex(
      document.documentElement.getAttribute('data-primary-color') || readStored(PRIMARY_KEY, DEFAULT_PRIMARY)
    );
    const theme = document.documentElement.getAttribute('data-theme') || DEFAULT_THEME;

    document.querySelectorAll('[data-primary-color-picker]').forEach((el) => {
      if (el.type === 'color') el.value = hex;
    });

    document.querySelectorAll('[data-theme-toggle]').forEach((el) => {
      el.setAttribute('aria-label', theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
      el.setAttribute('data-theme-state', theme);
    });
  }

  function initFromStorage() {
    let stored = readStored(PRIMARY_KEY, DEFAULT_PRIMARY);
    const lower = stored ? String(stored).toLowerCase() : '';
    if (lower === '#5b2d82' || lower === '#c41e3a') {
      stored = DEFAULT_PRIMARY;
      writeStored(PRIMARY_KEY, DEFAULT_PRIMARY);
    }
    applyPrimaryColor(stored);
    applyTheme(normalizeTheme(readStored(THEME_KEY, DEFAULT_THEME)));
  }

  let lastToggleAt = 0;
  function onClick(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const themeBtn = target.closest('[data-theme-toggle]');
    if (!themeBtn) return;
    event.preventDefault();
    const now = Date.now();
    /* Ignore duplicate click/touch within the same gesture on mobile */
    if (now - lastToggleAt < 350) return;
    lastToggleAt = now;
    const current = document.documentElement.getAttribute('data-theme') || DEFAULT_THEME;
    applyTheme(current === 'dark' ? 'light' : 'dark');
  }

  function onColorEvent(event) {
    const target = event.target;
    if (!target || target.getAttribute('data-primary-color-picker') === null) return;
    if (target.type !== 'color') return;
    applyPrimaryColor(target.value);
  }

  function bindPickers(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-primary-color-picker]').forEach((picker) => {
      if (picker.dataset.boundPrimary === '1') return;
      picker.dataset.boundPrimary = '1';
      picker.addEventListener('input', () => applyPrimaryColor(picker.value));
      picker.addEventListener('change', () => applyPrimaryColor(picker.value));
    });
  }

  initFromStorage();
  bindPickers(document);
  document.addEventListener('click', onClick);
  document.addEventListener('input', onColorEvent, true);
  document.addEventListener('change', onColorEvent, true);
  document.addEventListener('shopify:section:load', (event) => {
    bindPickers(event.target);
    syncControls();
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-auracase-preferences][open], [data-auracase-account][open]').forEach((prefs) => {
      if (prefs.contains(event.target)) return;
      prefs.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-auracase-preferences][open], [data-auracase-account][open]').forEach((prefs) => {
      prefs.removeAttribute('open');
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindPickers(document);
      syncControls();
    });
  } else {
    syncControls();
  }

  window.CaseLabVibe = {
    applyPrimaryColor,
    applyTheme,
    syncControls,
  };
})();
